/**
 * Air India Booking Widget Loader with Dependency Management
 *
 * This script dynamically loads the latest build of the Air India booking widget
 * by reading the build-info.json file and injecting the correct scripts and styles.
 * It includes smart dependency management to ensure external libraries (like Okta JS)
 * are available before initializing the widget.
 *
 * Usage:
 * 1. Include this script in your HTML (can be in <head> or <body>):
 *    <script src="https://your-cdn.com/ai-booking-widget-loader.js"></script>
 *
 *    Or with custom base URL and dependency management:
 *    <script src="ai-booking-widget-loader.js"
 *            data-bw20-base-url="https://cdn.example.com/widget/v1.2.3/"
 *            data-bw20-required-deps='["customAuthFunction", "customSignIn"]'></script>
 *
 * 2. Add the widget tags to your HTML where you want the widget to appear:
 *    <ai-config-provider data-config="YOUR_CONFIG_STRING"></ai-config-provider>
 *    <ai-booking-widget></ai-booking-widget>
 *
 * 3. (Optional) Configure before loading or use programmatic initialization:
 *    window.AI_WIDGET_LOADER_CONFIG = {
 *      baseUrl: "https://cdn.example.com/widget/v1.2.3/",
 *      requiredDependencies: ["isAuthenticated", "signIn"] // Custom dependency list
 *    };
 *    // OR
 *    window.AIWidgetLoader.init({
 *      baseUrl: "https://cdn.example.com/widget/v1.2.3/",
 *      requiredDependencies: ["isAuthenticated", "signIn"]
 *    });
 *
 * 4. (Optional) Listen for load events:
 *    document.addEventListener('ai-widget-success', (event) => {
 *      console.log('Widget loaded!', event.detail);
 *      console.log('Dependencies status:', event.detail.dependencyStatus);
 *    });
 *
 * The loader will automatically:
 * - Show skeleton loader for immediate visual feedback
 * - Wait for external dependencies (isAuthenticated, signIn, getAccessTokenForApi, getIdToken) to be available
 * - Fetch build-info.json from: ${baseUrl}/build-info.json (default: './build-info.json')
 * - Load CSS styles from: ${baseUrl}/${filename}
 * - Preload JavaScript chunks from: ${baseUrl}/${filename}
 * - Load main application script from: ${baseUrl}/${filename}
 * - Initialize the widget with dependency status information
 * - Remove skeleton loader when widget is ready
 *
 * Dependency Management:
 * - Default dependencies: ['isAuthenticated', 'signIn', 'getAccessTokenForApi', 'getIdToken']
 * - Alternative function names: Automatically detects 'getAccessTokenForAPI' vs 'getAccessTokenForApi'
 * - Automatic aliasing: Creates aliases for different naming conventions
 * - Timeout: 10 seconds (configurable)
 * - Graceful degradation: Widget loads even if some dependencies are missing
 * - Status reporting: Dependency availability is reported to the widget
 */

(function () {
  'use strict';

  // Configuration - baseUrl can be overridden via data attribute or global config
  const CONFIG = {
    baseUrl: './', // Default fallback (current directory)
    retryAttempts: 2, // Reduced retries for faster failure
    retryDelay: 500, // Faster retry
    timeout: 5000, // Reduced timeout
    dependencyCheckInterval: 10, // Check for dependencies every 10ms
    dependencyCheckTimeout: 10000, // Maximum wait time for dependencies (10 seconds)
    showSkeleton: true, // Show skeleton loader by default
  };

  // Utility functions
  const utils = {
    /**
     * Create a promise that rejects after a timeout
     */
    timeout: ms => new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms)),

    /**
     * Fetch with timeout and retry logic
     */
    fetchWithRetry: async (url, attempts = CONFIG.retryAttempts) => {
      for (let i = 0; i < attempts; i++) {
        try {
          const response = await Promise.race([fetch(url), utils.timeout(CONFIG.timeout)]);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          return response;
        } catch (error) {
          console.warn(`Attempt ${i + 1} failed for ${url}:`, error.message);

          if (i === attempts - 1) {
            throw error;
          }

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, CONFIG.retryDelay));
        }
      }
    },

    /**
     * Sanitize URL to prevent XSS attacks
     * Returns a safe URL object or null if invalid
     * @param {string} url - URL to sanitize
     * @returns {URL|null} - Safe URL object or null if invalid
     */
    sanitizeUrl: url => {
      if (!url || typeof url !== 'string') {
        return null;
      }

      const trimmedUrl = url.trim();
      const lowerUrl = trimmedUrl.toLowerCase();

      // Block dangerous protocols
      if (
        lowerUrl.startsWith('javascript:') ||
        lowerUrl.startsWith('data:') ||
        lowerUrl.startsWith('vbscript:')
      ) {
        console.error('Blocked unsafe URL protocol:', url);
        return null;
      }

      try {
        // For relative URLs, resolve against document base or current location
        // This respects <base> tags and maintains proper path resolution
        const urlObj = new URL(trimmedUrl, document.baseURI || window.location.href);

        // Allow same-origin URLs or any subdomain of airindia.com
        const hostname = urlObj.hostname.toLowerCase();
        const isSameOrigin = urlObj.origin === window.location.origin;
        const isAirIndiaSubdomain =
          hostname === 'airindia.com' || hostname.endsWith('.airindia.com');

        // Allow localhost with specific ports for development/testing
        const isAllowedLocalhost =
          hostname === 'localhost' &&
          (urlObj.port === '4502' || urlObj.port === '8080' || urlObj.port === '4200');

        if (!isSameOrigin && !isAirIndiaSubdomain && !isAllowedLocalhost) {
          console.error(
            'Blocked cross-origin URL (not from airindia.com or allowed localhost):',
            trimmedUrl
          );
          return null;
        }

        // Return the URL object - CodeQL recognizes this as safe
        return urlObj;
      } catch (e) {
        console.error('Blocked malformed URL:', trimmedUrl, e);
        return null;
      }
    },

    /**
     * Load a script dynamically
     */
    loadScript: (src, isModule = false) => {
      return new Promise((resolve, reject) => {
        const sanitizedUrl = utils.sanitizeUrl(src);
        if (!sanitizedUrl) {
          reject(new Error(`Invalid or unsafe script URL: ${src}`));
          return;
        }

        const script = document.createElement('script');
        // Use URL object's href property - CodeQL recognizes this as safe
        script.setAttribute('src', sanitizedUrl.href);
        script.onload = () => resolve(script);
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

        if (isModule) {
          script.type = 'module';
        }

        document.head.appendChild(script);
      });
    },

    /**
     * Load a stylesheet dynamically
     */
    loadStylesheet: href => {
      return new Promise((resolve, reject) => {
        const sanitizedUrl = utils.sanitizeUrl(href);
        if (!sanitizedUrl) {
          reject(new Error(`Invalid or unsafe stylesheet URL: ${href}`));
          return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        // Use URL object's href property - CodeQL recognizes this as safe
        link.setAttribute('href', sanitizedUrl.href);
        link.onload = () => resolve(link);
        link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));

        document.head.appendChild(link);
      });
    },

    /**
     * Add modulepreload link for better performance
     */
    addModulePreload: href => {
      const sanitizedUrl = utils.sanitizeUrl(href);
      if (!sanitizedUrl) {
        console.error('Blocked unsafe modulepreload URL:', href);
        return null;
      }

      const link = document.createElement('link');
      link.rel = 'modulepreload';
      // Use URL object's href property - CodeQL recognizes this as safe
      link.setAttribute('href', sanitizedUrl.href);
      document.head.appendChild(link);
      return link;
    },

    /**
     * Log with timestamp
     */
    log: (message, ...args) => {
      console.log(`[AI-Widget-Loader ${new Date().toISOString()}]`, message, ...args);
    },

    /**
     * Log error with timestamp
     */
    error: (message, ...args) => {
      console.error(`[AI-Widget-Loader ${new Date().toISOString()}]`, message, ...args);
    },

    /**
     * Check if required external dependencies are available
     */
    checkDependencies: (requiredFunctions = []) => {
      const defaultRequiredFunctions = [
        'isAuthenticated',
        'signIn',
        'getAccessTokenForApi',
        'getIdToken',
      ];
      const functionsToCheck =
        requiredFunctions.length > 0 ? requiredFunctions : defaultRequiredFunctions;

      // Check for alternative function names (case variations)
      const functionAlternatives = {
        getAccessTokenForApi: ['getAccessTokenForAPI', 'getAccessTokenForApi'],
        getIdToken: ['getIdToken', 'getIDToken'],
      };

      const missingFunctions = [];
      const availableFunctions = [];

      functionsToCheck.forEach(funcName => {
        const alternatives = functionAlternatives[funcName] || [funcName];
        const foundFunction = alternatives.find(altName => typeof window[altName] === 'function');

        if (foundFunction) {
          availableFunctions.push(foundFunction);
          // Create alias if different name was found
          if (foundFunction !== funcName && typeof window[funcName] !== 'function') {
            utils.log(`🔗 Creating alias: ${funcName} -> ${foundFunction}`);
            window[funcName] = window[foundFunction];
          }
        } else {
          missingFunctions.push(funcName);
        }
      });

      return {
        ready: missingFunctions.length === 0,
        missing: missingFunctions,
        available: availableFunctions,
      };
    },

    /**
     * Wait for external dependencies to be available
     */
    waitForDependencies: (requiredFunctions = []) => {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const checkInterval = setInterval(() => {
          const dependencyStatus = utils.checkDependencies(requiredFunctions);

          if (dependencyStatus.ready) {
            clearInterval(checkInterval);
            utils.log('✅ All external dependencies are ready:', dependencyStatus.available);
            resolve(dependencyStatus);
            return;
          }

          // Check timeout
          if (Date.now() - startTime > CONFIG.dependencyCheckTimeout) {
            clearInterval(checkInterval);
            utils.log(
              '⚠️ Dependency timeout reached. Missing functions:',
              dependencyStatus.missing
            );
            utils.log('📋 Available functions:', dependencyStatus.available);
            // Resolve anyway to allow widget to load with graceful degradation
            resolve(dependencyStatus);
            return;
          }

          // Log progress every 2 seconds
          if ((Date.now() - startTime) % 2000 < CONFIG.dependencyCheckInterval) {
            utils.log('⏳ Waiting for external dependencies:', dependencyStatus.missing);
          }
        }, CONFIG.dependencyCheckInterval);
      });
    },

    /**
     * Inject skeleton loader CSS and HTML for immediate visual feedback
     */
    injectSkeleton: () => {
      // Find widget container (no fallback - must exist in HTML)
      const widgetContainer = document.querySelector('ai-booking-widget');

      if (!widgetContainer) {
        utils.log('⚠️ No ai-booking-widget element found in DOM. Skeleton injection skipped.');
        return;
      }

      // Inject skeleton CSS
      const skeletonStyles = `
        <style id="ai-booking-widget-skeleton-styles">
          .ai-booking-widget-skeleton-container {
            width: 100%;
            margin: 0;
            padding: 16px 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #ffffff;
            animation: ai-skeleton-fade-in 0.3s ease-out;
            height: 187px;
            display: block;
            box-sizing: border-box;
            overflow: hidden;
          }

          @media (max-width: 1024px) {
            .ai-booking-widget-skeleton-container {
              height: 101px;
              padding: 16px;
            }
          }

          @keyframes ai-skeleton-fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .ai-skeleton-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e5e7eb;
          }

          .ai-skeleton-logo {
            width: 40px;
            height: 40px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            border-radius: 8px;
            animation: ai-skeleton-shimmer 1.5s infinite;
          }

          .ai-skeleton-title {
            height: 24px;
            width: 200px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            border-radius: 6px;
            animation: ai-skeleton-shimmer 1.5s infinite;
          }

          @keyframes ai-skeleton-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .ai-booking-widget-skeleton-form {
            display: flex;
            gap: 16px;
            align-items: center;
            width: 100%;
            box-sizing: border-box;
          }

          @media (max-width: 1024px) {
            .ai-booking-widget-skeleton-form {
              flex-direction: column;
              gap: 12px;
              align-items: stretch;
            }
          }

          .ai-booking-widget-skeleton-field {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0;
          }

          .ai-booking-widget-skeleton-input {
            height: 48px;
            background: linear-gradient(90deg, #f9fafb 25%, #f3f4f6 50%, #f9fafb 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.2s;
          }

          .ai-booking-widget-skeleton-from-to {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 2;
          }

          .ai-booking-widget-skeleton-swap {
            width: 24px;
            height: 24px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            border-radius: 50%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.1s;
            flex-shrink: 0;
          }

          .ai-booking-widget-skeleton-search-btn {
            width: 120px;
            height: 48px;
            background: linear-gradient(90deg, #dc2626 25%, #ef4444 50%, #dc2626 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.4s;
            flex-shrink: 0;
          }

          .ai-booking-widget-skeleton-upi-badge {
            width: 200px;
            height: 20px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.4s;
            margin-top: 8px;
            align-self: flex-end;
          }

          .ai-booking-widget-skeleton-bottom-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 25px;
          }

          .ai-booking-widget-skeleton-recent-search {
            width: 150px;
            height: 32px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.45s;
          }

          @media (max-width: 1024px) {
            .ai-booking-widget-skeleton-search-btn {
              width: 100%;
            }
            
            .ai-booking-widget-skeleton-from-to {
              flex: 1;
            }

            .ai-booking-widget-skeleton-bottom-row {
              flex-direction: column;
              align-items: stretch;
              gap: 8px;
            }

            .ai-booking-widget-skeleton-upi-badge {
              align-self: stretch;
              width: 100%;
            }
          }

          .ai-skeleton-date-picker {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .ai-skeleton-passengers {
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .ai-skeleton-counter {
            width: 80px;
            height: 36px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            border-radius: 6px;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.3s;
          }


          .ai-booking-widget-skeleton-trip-type {
            display: flex;
            gap: 24px;
            margin-bottom: 16px;
            margin-top: 10px;
            align-items: center;
          }

          .ai-booking-widget-skeleton-radio {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .ai-booking-widget-skeleton-radio-button {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.15s;
          }

          .ai-booking-widget-skeleton-radio-label {
            width: 70px;
            height: 14px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.2s;
          }

          .ai-booking-widget-skeleton-link {
            width: 80px;
            height: 14px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.25s;
          }

          .ai-booking-widget-skeleton-top-right {
            margin-left: auto;
            display: flex;
            gap: 16px;
            align-items: center;
          }

          .ai-booking-widget-skeleton-pay-by {
            width: 100px;
            height: 14px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.3s;
          }

          .ai-booking-widget-skeleton-promo {
            width: 120px;
            height: 14px;
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200% 100%;
            animation: ai-skeleton-shimmer 1.5s infinite;
            animation-delay: 0.35s;
          }

          @media (max-width: 1024px) {
            .ai-booking-widget-skeleton-trip-type {
              margin-bottom: 12px;
              gap: 16px;
              flex-wrap: wrap;
            }

            .ai-booking-widget-skeleton-top-right {
              margin-left: 0;
              width: 100%;
              justify-content: space-between;
            }
          }

          @keyframes ai-skeleton-pulse {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
          }

          .ai-booking-widget-skeleton-fade-out {
            animation: ai-booking-widget-skeleton-fade-out-animation 0.3s ease-in forwards;
          }

          @keyframes ai-booking-widget-skeleton-fade-out-animation {
            from { opacity: 1; transform: translateY(0); }
            to { opacity: 0; transform: translateY(-10px); }
          }
        </style>
      `;

      // Inject skeleton HTML
      const skeletonHTML = `
        <div id="ai-booking-widget-skeleton" class="ai-booking-widget-skeleton-container">
          <div class="ai-booking-widget-skeleton-trip-type">
            <div class="ai-booking-widget-skeleton-radio">
              <div class="ai-booking-widget-skeleton-radio-button"></div>
              <div class="ai-booking-widget-skeleton-radio-label"></div>
            </div>
            <div class="ai-booking-widget-skeleton-radio">
              <div class="ai-booking-widget-skeleton-radio-button"></div>
              <div class="ai-booking-widget-skeleton-radio-label"></div>
            </div>
            <div class="ai-booking-widget-skeleton-link"></div>
            <div class="ai-booking-widget-skeleton-top-right">
              <div class="ai-booking-widget-skeleton-pay-by"></div>
              <div class="ai-booking-widget-skeleton-promo"></div>
            </div>
          </div>

          <div class="ai-booking-widget-skeleton-form">
            <div class="ai-booking-widget-skeleton-from-to">
              <div class="ai-booking-widget-skeleton-field">
                <div class="ai-booking-widget-skeleton-input"></div>
              </div>
              <div class="ai-booking-widget-skeleton-swap"></div>
              <div class="ai-booking-widget-skeleton-field">
                <div class="ai-booking-widget-skeleton-input"></div>
              </div>
            </div>
            <div class="ai-booking-widget-skeleton-field">
              <div class="ai-booking-widget-skeleton-input"></div>
            </div>
            <div class="ai-booking-widget-skeleton-field">
              <div class="ai-booking-widget-skeleton-input"></div>
            </div>
            <div class="ai-booking-widget-skeleton-field">
              <div class="ai-booking-widget-skeleton-input"></div>
            </div>
            <div class="ai-booking-widget-skeleton-field">
              <div class="ai-booking-widget-skeleton-input"></div>
            </div>
            <div class="ai-booking-widget-skeleton-search-btn"></div>
          </div>

          <div class="ai-booking-widget-skeleton-bottom-row">
            <div class="ai-booking-widget-skeleton-recent-search"></div>
            <div class="ai-booking-widget-skeleton-upi-badge"></div>
          </div>
        </div>
      `;

      // Inject styles into head
      document.head.insertAdjacentHTML('beforeend', skeletonStyles);

      // Inject skeleton into widget container (secure DOM manipulation)
      // Use DOMParser to safely parse HTML without innerHTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(skeletonHTML, 'text/html');
      const skeletonElement = doc.body.firstChild;

      // Clear container and append parsed element
      while (widgetContainer.firstChild) {
        widgetContainer.removeChild(widgetContainer.firstChild);
      }
      widgetContainer.appendChild(skeletonElement);

      utils.log('✨ Skeleton loader injected successfully');
    },

    /**
     * Remove skeleton loader with smooth animation
     */
    removeSkeleton: () => {
      const skeleton = document.getElementById('ai-booking-widget-skeleton');
      const skeletonStyles = document.getElementById('ai-booking-widget-skeleton-styles');

      if (skeleton) {
        // Add fade-out animation
        skeleton.classList.add('ai-booking-widget-skeleton-fade-out');

        // Remove after animation completes
        setTimeout(() => {
          if (skeleton.parentNode) {
            skeleton.remove();
          }
          if (skeletonStyles) {
            skeletonStyles.remove();
          }
          utils.log('✨ Skeleton loader removed successfully');
        }, 300);
      }
    },
  };

  /**
   * Main loader class
   */
  class AIWidgetLoader {
    constructor(options = {}) {
      this.buildInfo = null;
      this.loadStartTime = Date.now();
      this.dependencyStatus = null;

      // If no baseUrl provided, try to read configuration from data attributes/window
      if (!options.baseUrl) {
        const autoOptions = getConfigurationOptions();
        options = { ...autoOptions, ...options };
      }

      // Store baseUrl with fallback to CONFIG default
      this.baseUrl = this.normalizeBaseUrl(options.baseUrl || CONFIG.baseUrl);

      // Store required dependencies (can be overridden via options)
      this.requiredDependencies = options.requiredDependencies || [];
    }

    /**
     * Normalize base URL to ensure it ends with a single slash
     */
    normalizeBaseUrl(baseUrl) {
      if (!baseUrl) return './';

      // Remove trailing slashes and add a single one
      const cleanUrl = baseUrl.replace(/\/+$/, '');
      return cleanUrl + '/';
    }

    /**
     * Get the build info URL (always baseUrl + 'build-info.json')
     */
    getBuildInfoUrl() {
      return this.baseUrl + 'build-info.json';
    }

    /**
     * Resolve a relative file path to an absolute URL
     */
    resolveAssetUrl(filePath) {
      if (!filePath) return null;

      // If already an absolute URL, return as-is
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
      }

      // Resolve relative path against base URL
      return this.baseUrl + filePath;
    }

    /**
     * Initialize the widget loader
     */
    async init() {
      try {
        utils.log('Starting AI Widget Loader...');

        // Step 0: Inject skeleton loader for immediate visual feedback
        if (CONFIG.showSkeleton) {
          utils.injectSkeleton();
        }

        // Step 1: Wait for external dependencies (like Okta JS) to be available
        utils.log('🔍 Checking for external dependencies...');
        this.dependencyStatus = await utils.waitForDependencies(this.requiredDependencies);

        // Load build information
        await this.loadBuildInfo();

        // Initialize widget configuration early (including dependency status)
        this.initializeWidgetConfig();

        // Start loading all assets in parallel for maximum speed
        const loadPromises = [];

        // Load styles (non-blocking)
        if (this.buildInfo.styles) {
          loadPromises.push(this.loadStyles());
        }

        // Preload chunks immediately (non-blocking)
        this.preloadChunks();

        // Load main script (this is the critical path)
        loadPromises.push(this.loadMainScript());

        // Wait for all critical resources to load
        await Promise.allSettled(loadPromises);

        const loadTime = Date.now() - this.loadStartTime;
        utils.log(`Widget loaded successfully in ${loadTime}ms`);

        // Remove skeleton loader with slight delay to ensure widget is rendered
        setTimeout(() => {
          utils.removeSkeleton();
        }, 100);

        // Dispatch custom event for integration
        this.dispatchLoadEvent('success', {
          buildInfo: this.buildInfo,
          loadTime,
          dependencyStatus: this.dependencyStatus,
        });
      } catch (error) {
        utils.error('Failed to load AI Widget:', error);

        // Update skeleton to show error state
        const skeleton = document.getElementById('ai-booking-widget-skeleton');
        if (skeleton) {
          // Show error state in skeleton if needed
          utils.log('Widget loading failed, skeleton remains visible');
        }

        this.dispatchLoadEvent('error', {
          error: error.message,
          dependencyStatus: this.dependencyStatus,
        });
        throw error;
      }
    }

    /**
     * Load and parse build information
     */
    async loadBuildInfo() {
      try {
        const buildInfoUrl = this.getBuildInfoUrl();
        utils.log('Loading build information from:', buildInfoUrl);

        const response = await utils.fetchWithRetry(buildInfoUrl);
        this.buildInfo = await response.json();

        utils.log('Build info loaded:', {
          version: this.buildInfo.version,
          main: this.buildInfo.main,
          styles: this.buildInfo.styles,
          chunks: this.buildInfo.chunks?.length || 0,
        });

        // Validate required fields
        if (!this.buildInfo.main) {
          throw new Error('Build info missing main script reference');
        }
      } catch (error) {
        throw new Error(`Failed to load build info: ${error.message}`);
      }
    }

    /**
     * Load stylesheets (non-blocking)
     */
    async loadStyles() {
      try {
        const stylesUrl = this.resolveAssetUrl(this.buildInfo.styles);
        utils.log('Loading styles from:', stylesUrl);
        await utils.loadStylesheet(stylesUrl);
        utils.log('Styles loaded successfully');
      } catch (error) {
        utils.error('Failed to load styles:', error);
        // Don't fail the entire load for missing styles
      }
    }

    /**
     * Preload chunks for better performance
     */
    preloadChunks() {
      if (!this.buildInfo.chunks || this.buildInfo.chunks.length === 0) {
        utils.log('No chunks to preload');
        return;
      }

      utils.log('Preloading chunks:', this.buildInfo.chunks);

      this.buildInfo.chunks.forEach(chunk => {
        try {
          const chunkUrl = this.resolveAssetUrl(chunk);
          utils.log('Preloading chunk from:', chunkUrl);
          utils.addModulePreload(chunkUrl);
        } catch (error) {
          utils.error(`Failed to preload chunk ${chunk}:`, error);
        }
      });
    }

    /**
     * Load the main application script
     */
    async loadMainScript() {
      try {
        const mainScriptUrl = this.resolveAssetUrl(this.buildInfo.main);
        utils.log('Loading main script from:', mainScriptUrl);
        await utils.loadScript(mainScriptUrl, true);
        utils.log('Main script loaded successfully');
      } catch (error) {
        throw new Error(`Failed to load main script: ${error.message}`);
      }
    }

    /**
     * Initialize widget configuration
     */
    initializeWidgetConfig() {
      // Set up global configuration for the widget
      window.AI_WIDGET_CONFIG = {
        ...window.AI_WIDGET_CONFIG,
        buildInfo: this.buildInfo,
        loadedAt: new Date().toISOString(),
        loader: {
          version: '1.0.0',
          loadTime: Date.now() - this.loadStartTime,
        },
        // Include dependency status for the widget to handle gracefully
        dependencies: this.dependencyStatus,
      };

      utils.log('Widget configuration initialized');
    }

    /**
     * Dispatch custom events for integration
     */
    dispatchLoadEvent(type, detail) {
      const event = new CustomEvent(`ai-widget-${type}`, {
        detail,
        bubbles: true,
        cancelable: false,
      });

      document.dispatchEvent(event);

      // Also dispatch on window for broader compatibility
      window.dispatchEvent(event);
    }
  }

  /**
   * Auto-initialize with smart timing strategy
   */
  function autoInit() {
    // Get configuration options from various sources
    const options = getConfigurationOptions();

    // Apply configuration to global CONFIG
    if (typeof options.showSkeleton === 'boolean') {
      CONFIG.showSkeleton = options.showSkeleton;
    }

    const loader = new AIWidgetLoader(options);

    // Smart initialization strategy for optimal loading
    const initializeLoader = () => {
      // Use requestIdleCallback for better performance if available
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => loader.init(), { timeout: 100 });
      } else {
        // Fallback to immediate initialization
        loader.init();
      }
    };

    // Check DOM readiness and initialize accordingly
    // Document readyState values:
    // - 'loading': The document is still loading
    // - 'interactive': The document has finished loading and parsing but sub-resources may still be loading
    // - 'complete': The document and all sub-resources have finished loading
    if (document.readyState === 'loading') {
      // DOM is still loading, wait for DOMContentLoaded
      if (document.addEventListener) {
        document.addEventListener('DOMContentLoaded', initializeLoader, { once: true });
      } else {
        // Fallback for older browsers
        setTimeout(initializeLoader, 0);
      }
    } else {
      // DOM is already interactive or complete, initialize immediately
      // Note: When readyState is 'interactive', DOMContentLoaded has already fired
      initializeLoader();
    }
  }

  /**
   * Get configuration options from data attributes, window config, or defaults
   */
  function getConfigurationOptions() {
    const options = {};

    // Try to get baseUrl from script tag data attribute (with BW20 namespace)
    const currentScript =
      document.currentScript || document.querySelector('script[src*="ai-booking-widget-loader"]');

    if (currentScript) {
      const baseUrl = currentScript.getAttribute('data-bw20-base-url');
      const requiredDeps = currentScript.getAttribute('data-bw20-required-deps');

      if (baseUrl) {
        options.baseUrl = baseUrl;
        utils.log('Found baseUrl from script data-bw20-base-url attribute:', baseUrl);
      }

      if (requiredDeps) {
        try {
          options.requiredDependencies = JSON.parse(requiredDeps);
          utils.log(
            'Found required dependencies from script data-bw20-required-deps attribute:',
            options.requiredDependencies
          );
        } catch (error) {
          utils.error('Failed to parse required dependencies from data attribute:', error);
        }
      }

      // Check for skeleton loader configuration
      const showSkeleton = currentScript.getAttribute('data-bw20-show-skeleton');
      if (showSkeleton !== null) {
        options.showSkeleton = showSkeleton !== 'false';
        utils.log('Found skeleton configuration from script attribute:', options.showSkeleton);
      }
    }

    // Also check window configuration
    if (window.AI_WIDGET_LOADER_CONFIG) {
      if (window.AI_WIDGET_LOADER_CONFIG.baseUrl) {
        options.baseUrl = window.AI_WIDGET_LOADER_CONFIG.baseUrl;
        utils.log('Found baseUrl from window config:', window.AI_WIDGET_LOADER_CONFIG.baseUrl);
      }

      if (window.AI_WIDGET_LOADER_CONFIG.requiredDependencies) {
        options.requiredDependencies = window.AI_WIDGET_LOADER_CONFIG.requiredDependencies;
        utils.log('Found required dependencies from window config:', options.requiredDependencies);
      }

      if (typeof window.AI_WIDGET_LOADER_CONFIG.showSkeleton === 'boolean') {
        options.showSkeleton = window.AI_WIDGET_LOADER_CONFIG.showSkeleton;
        utils.log('Found skeleton configuration from window config:', options.showSkeleton);
      }
    }

    utils.log('Final configuration options:', options);
    return options;
  }

  /**
   * Public API
   */
  window.AIWidgetLoader = {
    // Manual initialization with optional configuration
    init: (options = {}) => new AIWidgetLoader(options).init(),

    // Expose constructor for advanced usage
    Loader: AIWidgetLoader,

    // Version info
    version: '1.0.0',

    // Configuration
    configure: config => {
      Object.assign(CONFIG, config);
    },
  };

  // Auto-initialize by default
  // Can be disabled by setting window.AI_WIDGET_AUTO_INIT = false before loading this script
  if (window.AI_WIDGET_AUTO_INIT !== false) {
    autoInit();
  }
})();
